import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import DateRangeSelector from './components/DateRangeSelector';
import TrendChart from './components/TrendChart';
import TimelineScrubber from './components/TimelineScrubber';
import WaffleGrid from './components/WaffleGrid';
import StatisticsPanel from './components/StatisticsPanel';
import LegendPanel from './components/LegendPanel';
import ExportPanel from './components/ExportPanel';
import Icon from '../../components/AppIcon';
import { fetchMetrics } from '../../data';

const HistoricalTrendOverlay = () => {
  const [selectedRange, setSelectedRange] = useState('1Y');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: '2025-01-01',
    end: '2026-01-28'
  });
  const [showOverlay, setShowOverlay] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentRealTimeData, setCurrentRealTimeData] = useState(null);

  // M2 Money Supply - Update this value periodically from official sources
  const M2_MONEY_SUPPLY_USD = 21100000000000; // $21.1 trillion

  const fetchHistoricalData = async (range) => {
    setIsLoading(true);
    setError(null);

    try {
      // DeFillama historical stablecoin charts API - returns array of historical data points
      const response = await fetch('https://stablecoins.llama.fi/stablecoincharts/all');

      if (!response?.ok) {
        throw new Error(`DeFillama API request failed: ${response.status}`);
      }

      // Check if response has content
      const contentType = response?.headers?.get('content-type');
      if (!contentType || !contentType?.includes('application/json')) {
        throw new Error('Invalid response format from DeFillama API');
      }

      // Get response text first to check if it's empty
      const responseText = await response?.text();
      if (!responseText || responseText?.trim() === '') {
        throw new Error('Empty response from DeFillama API');
      }

      // Parse the JSON
      const apiData = JSON.parse(responseText);

      // Process the data to get monthly aggregates for the past 5 years
      const processedData = processHistoricalData(apiData, range);

      setHistoricalData(processedData);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching historical data:', err);
      setError(err?.message);
      setIsLoading(false);

      // Fallback to mock data if API fails
      setHistoricalData(generateFallbackData(range));
    }
  };

  const processHistoricalData = (apiData, range) => {
    if (!apiData || !Array.isArray(apiData)) {
      throw new Error('Invalid API response format');
    }

    // Determine the number of months based on range
    let months = 12;
    switch (range) {
      case '1M':
        months = 1;
        break;
      case '3M':
        months = 3;
        break;
      case '6M':
        months = 6;
        break;
      case '1Y':
        months = 12;
        break;
      case 'All':
        months = 60; // 5 years
        break;
      default:
        months = 12;
    }

    // Get the end date (today) and calculate start date
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate?.setMonth(startDate?.getMonth() - months);
    const startTimestamp = Math.floor(startDate?.getTime() / 1000);

    // Filter data to only include points after start date
    const filteredData = apiData?.filter((item) => item?.date >= startTimestamp);

    // Group data by month and take the last entry of each month
    const monthlyData = {};

    filteredData?.forEach((item) => {
      const date = new Date(item?.date * 1000);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;

      // Keep the last entry for each month (data is sorted by date)
      monthlyData[monthKey] = item;
    });

    // Convert to array and format for chart
    const formattedData = Object.entries(monthlyData)?.
    sort(([keyA], [keyB]) => keyA?.localeCompare(keyB))?.
    map(([monthKey, item], index, array) => {
      const date = new Date(item?.date * 1000);
      const monthStr = date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      // totalCirculatingUSD contains the total stablecoin market cap
      const stablecoins_usd = item?.totalCirculatingUSD?.peggedUSD || 0;
      const m2_usd = M2_MONEY_SUPPLY_USD;
      const percent = stablecoins_usd / m2_usd * 100;

      // Calculate change from previous month
      let change = 0;
      if (index > 0) {
        const prevItem = array?.[index - 1]?.[1];
        const prevPercent = prevItem?.totalCirculatingUSD?.peggedUSD / m2_usd * 100;
        change = (percent - prevPercent) / prevPercent * 100;
      }

      return {
        date: monthStr,
        month: monthStr,
        percent: percent,
        stablecoins_usd: stablecoins_usd,
        m2_usd: m2_usd,
        ratio: percent,
        change: change,
        stablecoinMarketCap: stablecoins_usd,
        m2Supply: m2_usd
      };
    });

    // Ensure we have exactly the requested number of months (or less if data not available)
    return formattedData?.slice(-months - 1); // +1 to include current month
  };

  const generateFallbackData = (range) => {
    // Fallback mock data generation (same as before)
    const endDate = new Date();
    let startDate = new Date(endDate);
    let months = 12;

    switch (range) {
      case '1M':
        months = 1;
        break;
      case '3M':
        months = 3;
        break;
      case '6M':
        months = 6;
        break;
      case '1Y':
        months = 12;
        break;
      case 'All':
        months = 60; // 5 years
        break;
      default:
        months = 12;
        break;
    }

    startDate?.setMonth(startDate?.getMonth() - months);

    const data = [];
    const baseStablecoins = 168500000000; // ~$168.5B current
    const baseM2 = 21100000000000; // ~$21.1T current

    for (let i = 0; i <= months; i++) {
      const currentDate = new Date(startDate);
      currentDate?.setMonth(startDate?.getMonth() + i);

      const growthFactor = 0.85 + i / months * 0.15;
      const volatility = (Math.random() - 0.5) * 0.05;

      const stablecoins_usd = baseStablecoins * growthFactor * (1 + volatility);
      const m2_usd = baseM2 * (0.98 + i / months * 0.02);
      const percent = stablecoins_usd / m2_usd * 100;

      const monthStr = currentDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      data?.push({
        date: monthStr,
        month: monthStr,
        percent: percent,
        stablecoins_usd: stablecoins_usd,
        m2_usd: m2_usd,
        ratio: percent,
        change: i > 0 ? (percent - data?.[i - 1]?.percent) / data?.[i - 1]?.percent * 100 : 0,
        stablecoinMarketCap: stablecoins_usd,
        m2Supply: m2_usd
      });
    }

    return data;
  };

  useEffect(() => {
    fetchHistoricalData(selectedRange);
    setCurrentIndex(0);
  }, [selectedRange]);

  // Fetch real-time current data for "Current Ratio" display
  useEffect(() => {
    const fetchCurrentData = async () => {
      try {
        const metrics = await fetchMetrics();
        const ratio = metrics?.stablecoins_usd / metrics?.m2_usd * 100;

        setCurrentRealTimeData({
          ratio: ratio,
          stablecoins_usd: metrics?.stablecoins_usd,
          m2_usd: metrics?.m2_usd,
          stablecoinMarketCap: metrics?.stablecoins_usd,
          m2Supply: metrics?.m2_usd,
          last_updated: metrics?.last_updated
        });
      } catch (err) {
        console.error('Error fetching real-time current data:', err);
      }
    };

    fetchCurrentData();

    // Refresh real-time data every 15 minutes
    const refreshInterval = setInterval(fetchCurrentData, 15 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    let interval;
    if (isPlaying && currentIndex < historicalData?.length - 1) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= historicalData?.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentIndex, historicalData?.length]);

  const calculateHistoricalStats = () => {
    const ratios = historicalData?.map((d) => d?.ratio);
    const high = Math.max(...ratios);
    const low = Math.min(...ratios);
    const average = ratios?.reduce((a, b) => a + b, 0) / ratios?.length;
    const variance = ratios?.reduce((sum, val) => sum + Math.pow(val - average, 2), 0) / ratios?.length;
    const volatility = Math.sqrt(variance) * 100;

    return { high, low, average, volatility };
  };

  const currentData = historicalData?.[currentIndex] || historicalData?.[0];
  const historicalStats = calculateHistoricalStats();

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    setIsPlaying(false);
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const handlePlayPause = () => {
    if (currentIndex >= historicalData?.length - 1) {
      setCurrentIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <>
      <Helmet>
        <title>Historical Trend Overlay - StablecoinM2Tracker</title>
        <meta name="description" content="View historical trends and time-series analysis of stablecoin market cap relative to M2 money supply with interactive visualizations and data export capabilities." />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-1 pt-16">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="mb-6 md:mb-8 lg:mb-12">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                    Historical Trend Analysis
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Explore time-series data and percentage changes over customizable periods
                  </p>
                </div>
                <button
                  onClick={() => setShowOverlay(!showOverlay)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg transition-all duration-300 hover:bg-primary/90">

                  <Icon name={showOverlay ? 'EyeOff' : 'Eye'} size={18} color="currentColor" />
                  <span className="text-sm font-medium">
                    {showOverlay ? 'Hide' : 'Show'} Overlay
                  </span>
                </button>
              </div>

              <DateRangeSelector
                selectedRange={selectedRange}
                onRangeChange={handleRangeChange}
                customDateRange={customDateRange}
                onCustomDateChange={handleCustomDateChange} />

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Stablecoin to US M2 Supply Trend Visualization

                  </h2>
                  <TrendChart data={historicalData} selectedMetric="ratio" />
                </div>

                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                    Timeline Control
                  </h2>
                  <TimelineScrubber
                    data={historicalData}
                    currentIndex={currentIndex}
                    onIndexChange={setCurrentIndex}
                    isPlaying={isPlaying}
                    onPlayPause={handlePlayPause} />

                </div>

                {showOverlay &&
                <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                      Current Snapshot
                    </h2>
                    <WaffleGrid
                    stablecoinPercentage={currentData?.ratio / 100}
                    animationKey={currentIndex} />

                  </div>
                }
              </div>

              <div className="space-y-6 md:space-y-8">
                <LegendPanel
                  stablecoinPercentage={currentData?.ratio / 100}
                  m2Percentage={1 - currentData?.ratio / 100} />

                <ExportPanel data={historicalData} selectedRange={selectedRange} />
              </div>
            </div>

            <StatisticsPanel
              currentData={currentData}
              currentRealTimeData={currentRealTimeData}
              historicalStats={historicalStats} />


            <div className="mt-6 md:mt-8 bg-card border border-border rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10 flex-shrink-0">
                  <Icon name="Info" size={20} color="var(--color-secondary)" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                    About Historical Data
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-3">This visualization shows the historical relationship between total stablecoin market capitalization and the M2 money supply. The data is calculated using daily snapshots of major stablecoins against the Federal Reserve's M2 monetary aggregate.

                  </p>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Use the timeline controls to explore specific dates, or enable animation to see how the ratio has evolved over time. Export functionality allows you to download the data for further analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default HistoricalTrendOverlay;